import axios from 'axios';

// Detectar entorno automáticamente
const getApiBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // Usar REACT_APP_BACKEND_URL solo si apunta a algo que no sea localhost
  // (evita que el .env local se cuele en el bundle de producción)
  if (backendUrl) {
    return backendUrl.replace(/\/+$/, '');
  }
  // En producción o cualquier hostname que no sea localhost → rutas relativas
  // Axios/fetch con path relativo siempre apunta al mismo origen del app
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return '';
  }
  // En desarrollo local, usar backend local
  return 'http://localhost:3010';
};

const API_BASE_URL = getApiBaseUrl();

class VirtualPosService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/virtualpos`;
    console.log('🔧 VirtualPos Service URL:', this.baseURL);
  }

  /**
   * Configuración base para las peticiones HTTP
   */
  getRequestConfig() {
    return {
      timeout: 30000
    };
  }

  /**
   * Obtiene todos los planes
   */
  async getPlans() {
    try {
      console.log('📋 Obteniendo planes desde backend proxy...');
      const response = await axios.get(`${this.baseURL}/plans`, this.getRequestConfig());
      console.log('✅ Planes obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo planes:', error.response?.data || error.message);
      throw new Error(`Error obteniendo planes: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Obtiene un plan por ID
   */
  async getPlanById(id) {
    try {
      console.log(`📋 Obteniendo plan ${id} desde backend proxy...`);
      const response = await axios.get(`${this.baseURL}/plans/${id}`, this.getRequestConfig());
      console.log('✅ Plan obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo plan:', error.response?.data || error.message);
      throw new Error(`Error obteniendo plan: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Crea un nuevo plan
   */
  async createPlan(planData) {
    try {
      console.log('📝 Creando plan desde backend proxy...', planData);
      const response = await axios.post(`${this.baseURL}/plans`, planData, this.getRequestConfig());
      console.log('✅ Plan creado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando plan:', error.response?.data || error.message);
      throw new Error(`Error creando plan: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Actualiza un plan existente
   */
  async updatePlan(id, planData) {
    try {
      console.log(`📝 Actualizando plan ${id} desde backend proxy...`, planData);
      const response = await axios.put(`${this.baseURL}/plans/${id}`, planData, this.getRequestConfig());
      console.log('✅ Plan actualizado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando plan:', error.response?.data || error.message);
      throw new Error(`Error actualizando plan: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Sincroniza un plan con FileMaker
   */
  async syncPlanWithFileMaker(planId, filemakerData) {
    try {
      console.log(`🔄 Sincronizando plan ${planId} con FileMaker...`, filemakerData);
      const response = await axios.post(`${this.baseURL}/plans/${planId}/sync-filemaker`, filemakerData, this.getRequestConfig());
      console.log('✅ Plan sincronizado con FileMaker:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error sincronizando plan con FileMaker:', error.response?.data || error.message);
      throw new Error(`Error sincronizando plan con FileMaker: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Obtiene todas las suscripciones
   */
  async getSubscriptions(filters = {}) {
    try {
      console.log('📋 Obteniendo suscripciones desde backend proxy...');
      
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      
      const url = `${this.baseURL}/subscriptions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      // El listado de VirtualPOS tarda entre 8 y 30 s por página, así que los
      // 30 s por defecto se quedan cortos y la pantalla queda vacía.
      const response = await axios.get(url, {
        ...this.getRequestConfig(),
        timeout: filters.timeout || 90000,
      });
      console.log('✅ Suscripciones obtenidas:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo suscripciones:', error.response?.data || error.message);
      throw new Error(`Error obteniendo suscripciones: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Obtiene una suscripción por ID
   */
  async getSubscriptionById(id) {
    try {
      console.log(`📋 Obteniendo suscripción ${id} desde backend proxy...`);
      const response = await axios.get(`${this.baseURL}/subscriptions/${id}`, this.getRequestConfig());
      console.log('✅ Suscripción obtenida:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo suscripción:', error.response?.data || error.message);
      throw new Error(`Error obteniendo suscripción: ${error.response?.data?.error || error.message}`);
    }
  }

  async syncSubscription(id) {
    try {
      console.log(`🔄 Sincronizando suscripción ${id} desde backend proxy...`);
      const response = await axios.post(`${this.baseURL}/subscriptions/${id}/sync`, {}, this.getRequestConfig());
      console.log('✅ Suscripción sincronizada:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error sincronizando suscripción:', error.response?.data || error.message);
      throw new Error(`Error sincronizando suscripción: ${error.response?.data?.error || error.message}`);
    }
  }

  async getChargeById(chargeId) {
    try {
      console.log(`📋 Obteniendo cargo ${chargeId} desde backend proxy...`);
      const response = await axios.get(`${this.baseURL}/charges/${chargeId}`, this.getRequestConfig());
      console.log('✅ Cargo obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo cargo:', error.response?.data || error.message);
      throw new Error(`Error obteniendo cargo: ${error.response?.data?.error || error.message}`);
    }
  }

  /** Recurso de pago único (GET /v3/payment/{id}) — otro endpoint que getChargeById. */
  async getPaymentById(paymentId) {
    try {
      console.log(`📋 Obteniendo pago ${paymentId} desde backend proxy...`);
      const response = await axios.get(`${this.baseURL}/payments/${paymentId}`, this.getRequestConfig());
      console.log('✅ Pago obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo pago:', error.response?.data || error.message);
      throw new Error(`Error obteniendo pago: ${error.response?.data?.error || error.message}`);
    }
  }

  async retryCharge(chargeId) {
    try {
      console.log(`🔄 Reintentando cobro ${chargeId} desde backend proxy...`);
      const response = await axios.post(`${this.baseURL}/charges/${chargeId}/retry`, {}, this.getRequestConfig());
      console.log('✅ Cobro reintentado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error reintentando cobro:', error.response?.data || error.message);
      throw new Error(`Error reintentando cobro: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Crea una nueva suscripción
   */
  async createSubscription(subscriptionData) {
    try {
      console.log('📝 Creando suscripción desde backend proxy...', subscriptionData);
      const response = await axios.post(`${this.baseURL}/subscriptions`, subscriptionData, this.getRequestConfig());
      console.log('✅ Suscripción creada:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando suscripción:', error.response?.data || error.message);
      throw new Error(`Error creando suscripción: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Genera un link de cambio de tarjeta (changecard) para una suscripción.
   * Solo funciona si VirtualPos aún tiene la suscripción en estado activo/suscribiendo.
   * Returns { url, raw }
   */
  async generateChangeCardLink(id) {
    try {
      console.log(`🔗 Generando link changecard para suscripción ${id}...`);
      const response = await axios.put(
        `${this.baseURL}/subscriptions/${id}/changecard`,
        {},
        this.getRequestConfig()
      );
      console.log('✅ Changecard link generado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error generando changecard link:', error.response?.data || error.message);
      throw new Error(
        `Error generando link de cambio de tarjeta: ${error.response?.data?.error || error.message}`
      );
    }
  }

  async sendChangeCardLinkEmail(id, payload, token) {
    try {
      console.log(`📧 Enviando changecard por email para suscripción ${id}...`, payload);
      const response = await axios.post(
        `${this.baseURL}/subscriptions/${id}/changecard-email`,
        payload,
        {
          ...this.getRequestConfig(),
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      console.log('✅ Changecard enviado por email:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error enviando changecard por email:', error.response?.data || error.message);
      throw new Error(
        `Error enviando el link por email: ${error.response?.data?.error || error.message}`
      );
    }
  }

  /**
   * Cancela una suscripción
   */
  async cancelSubscription(id) {
    try {
      console.log(`🚫 Cancelando suscripción ${id} desde backend proxy...`);
      const response = await axios.delete(`${this.baseURL}/subscriptions/${id}`, this.getRequestConfig());

      // VirtualPOS manda sus errores con HTTP 200 y el envelope en NOK. El
      // backend ya lo traduce a error, pero validamos también aquí para no dar
      // por cancelada una suscripción que sigue viva.
      const data = response.data;
      if (data?.status === 'NOK' || data?.status === 'ERROR') {
        const err = data?.error || {};
        const code = err.error_code || err.code || data?.code || 'NOK';
        const message = err.message || err.description || data?.message || 'Cancelación rechazada';
        throw new Error(`VirtualPos rechazó la cancelación (${code}): ${message}`);
      }

      console.log('✅ Suscripción cancelada:', data);
      return data;
    } catch (error) {
      console.error('❌ Error cancelando suscripción:', error.response?.data || error.message);
      const detalle = error.response?.data?.error || error.response?.data?.message || error.message;
      throw new Error(`Error cancelando suscripción: ${detalle}`);
    }
  }

  /**
   * Sincroniza un plan con FileMaker
   */
  async syncPlanWithFileMaker(planId, filemakerData) {
    try {
      console.log(`🔄 Sincronizando plan ${planId} con FileMaker...`, filemakerData);
      const response = await axios.post(`${this.baseURL}/plans/${planId}/sync-filemaker`, filemakerData, this.getRequestConfig());
      console.log('✅ Plan sincronizado con FileMaker:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error sincronizando plan con FileMaker:', error.response?.data || error.message);
      throw new Error(`Error sincronizando plan con FileMaker: ${error.response?.data?.error || error.message}`);
    }
  }
}

// Instancia singleton
const virtualPosService = new VirtualPosService();
export default virtualPosService;

// Funciones de utilidad
export const getPlans = () => virtualPosService.getPlans();
export const getPlanById = (id) => virtualPosService.getPlanById(id);
export const createPlan = (planData) => virtualPosService.createPlan(planData);
export const updatePlan = (id, planData) => virtualPosService.updatePlan(id, planData);
export const syncPlanWithFileMaker = (planId, filemakerData) => virtualPosService.syncPlanWithFileMaker(planId, filemakerData);

// ELIMINAR estas líneas duplicadas (113-133):
// export const syncPlanWithFileMaker = async (planId, planData) => {
//   try {
//     // Implementación de sincronización con FileMaker
//     const response = await fetch('/api/filemaker/sync-plan', {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({ planId, planData })
//     });
//     
//     if (!response.ok) {
//       throw new Error('Error syncing plan with FileMaker');
//     }
//     
//     return await response.json();
//   } catch (error) {
//     console.error('Error syncing plan with FileMaker:', error);
//     throw error;
//   }
// };

// Agregar exports para suscripciones
export const getSubscriptions = (filters) => virtualPosService.getSubscriptions(filters);
export const getSubscriptionById = (id) => virtualPosService.getSubscriptionById(id);
export const syncSubscription = (id) => virtualPosService.syncSubscription(id);
export const getChargeById = (id) => virtualPosService.getChargeById(id);
export const getPaymentById = (id) => virtualPosService.getPaymentById(id);
export const retryCharge = (id) => virtualPosService.retryCharge(id);
export const createSubscription = (data) => virtualPosService.createSubscription(data);
export const cancelSubscription = (id) => virtualPosService.cancelSubscription(id);
